import { useAuth } from "@clerk/tanstack-react-start";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Clock } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "~/lib/cn";
import { getSummitCountdownTargetOptions } from "~/lib/functions/summit";
import {
  getNextAvailableSummitOptions,
  checkUserSummitRegistrationOptions,
} from "~/lib/functions/summit-registration";

const COUNTDOWN_REFRESH_MS = 60_000;

function formatCountdown(timestamp: number): string | null {
  const now = Date.now();
  const diff = timestamp - now;

  if (diff <= 0) {
    return null;
  }

  const totalMinutes = Math.floor(diff / 60_000);

  if (totalMinutes <= 0) {
    return "<1m";
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (totalHours > 0) {
    return `${totalHours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

/**
 * Format the target datetime for display in the user's timezone.
 * Shows: "Fri, Jul 25 at 4:00 PM"
 */
function formatTargetDateForUser(date: Date): string {
  const formatter = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return formatter.format(date);
}

function useCountdownLabel(startTimeUtc: string | null): {
  countdown: string | null;
  formattedDate: string | null;
} {
  const startTimestamp = useMemo(
    () => (startTimeUtc ? Date.parse(startTimeUtc) : null),
    [startTimeUtc],
  );

  const computeCountdown = useCallback(
    () => (startTimestamp ? formatCountdown(startTimestamp) : null),
    [startTimestamp],
  );

  const [countdown, setCountdown] = useState<string | null>(null);

  const formattedDate = useMemo(() => {
    if (!startTimestamp || Number.isNaN(startTimestamp)) return null;
    return formatTargetDateForUser(new Date(startTimestamp));
  }, [startTimestamp]);

  useEffect(() => {
    const nextLabel = computeCountdown();
    setCountdown(nextLabel);

    if (!startTimestamp || nextLabel === null) {
      return;
    }

    const intervalId = window.setInterval(() => {
      const updatedLabel = computeCountdown();
      setCountdown(updatedLabel);

      if (updatedLabel === null) {
        window.clearInterval(intervalId);
      }
    }, COUNTDOWN_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [computeCountdown, startTimestamp]);

  return { countdown, formattedDate };
}

/** Banner for the next summit that adapts to whether the user is signed in and registered. */
export const SummitTopBanner = () => {
  const { isSignedIn } = useAuth();
  const { data } = useQuery(getNextAvailableSummitOptions);
  const { data: registrationData } = useQuery({
    ...checkUserSummitRegistrationOptions,
    enabled: isSignedIn === true,
  });
  const { data: countdownData } = useQuery(getSummitCountdownTargetOptions);
  const summit = data?.summit;

  const { countdown, formattedDate } = useCountdownLabel(
    countdownData?.startTimeUtc ?? null,
  );

  const isRegistered = isSignedIn && registrationData?.isRegistered;
  const linkTo = isRegistered ? "/summit/agenda" : "/summit";
  const ctaText = isRegistered ? "View Agenda" : "Register now";

  if (!summit?.title) {
    return null;
  }

  return (
    <Link
      to={linkTo}
      aria-label={
        isRegistered
          ? "View Summit Agenda"
          : "Register for the DreamCoach Summit"
      }
      className={cn(
        "group relative block w-full overflow-hidden bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 text-white shadow-lg transition",
        "focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-700 focus-visible:outline-none",
      )}
    >
      <div
        className={cn(
          "mx-auto grid w-full max-w-screen-xl gap-3 px-4 py-3 text-center",
          "sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-6 sm:px-6",
        )}
      >
        <span className="text-sm leading-6 font-semibold text-white sm:col-start-1 sm:justify-self-start sm:text-left sm:text-base">
          {summit.title}
        </span>
        {countdown ? (
          <span className="flex items-center justify-center gap-2 text-sm leading-6 font-medium text-white tabular-nums sm:col-start-2 sm:justify-self-center sm:text-base">
            <Clock className="h-4 w-4" aria-hidden />
            <span>
              Starts in {countdown}
              {formattedDate ? (
                <span className="hidden lg:inline"> ({formattedDate})</span>
              ) : null}
            </span>
          </span>
        ) : null}
        <span className="flex items-center justify-center gap-2 text-sm leading-6 font-semibold text-white sm:col-start-3 sm:justify-end sm:justify-self-end sm:text-base">
          {ctaText}
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
      <span
        className="absolute inset-x-0 bottom-0 h-0.5 bg-white/25"
        aria-hidden
      />
    </Link>
  );
};
